import { groupFooterLines } from './groupedExportView'

/**
 * Spec 213 — where the grouped grid's display pages break.
 *
 * CC-49616 paged the grouped grid over TOP-LEVEL units (groups, and any leaf that
 * sits at the top), 25 per page, because rendering a whole large report froze the
 * browser. That bounds a page by its number of groups, never by its size. Fees
 * Billed By Originator (49) on All Dates has 54 originators holding 24,493 entries,
 * so page 1 — 25 originators — rendered 12,550 rows / 108,222 cells. Measured on the
 * deployed QA build (2026-11-02): one keyboard column resize cost 13-16 s of main
 * thread (≈5-7 s React, 4-7 s layout of the auto-sized table), the run itself 16-19 s,
 * and the post-deploy ADA audit timed out on it.
 *
 * So a page now also closes once it holds `rowBudget` rendered rows. The 25-unit
 * rule is unchanged, so a report whose pages fit the budget pages exactly as before.
 * A top-level unit is never split across pages: one that alone exceeds the budget
 * gets a page of its own, because splitting a group would cut its subtotal away from
 * its rows and break the positional collapse keys the export view shares
 * (groupRowKey).
 */
export const GROUP_PAGE_ROW_BUDGET = 1000

/**
 * The `<tr>`s a top-level unit renders when fully expanded: a leaf is one row; a
 * group is its heading, its subtotal lines and everything inside it.
 */
export const renderedRowWeight = (unit) => {
  if (!unit?.isGroup) return 1
  let w = 1 + groupFooterLines(unit).length
  for (const child of unit.dataRows || []) w += renderedRowWeight(child)
  return w
}

/**
 * The start index of each display page over `units` (the grid's `visibleTop`).
 * Always at least `[0]`. A page closes before a unit when it already holds
 * `unitsPerPage` units, or when adding the unit would take it past `rowBudget`
 * rendered rows; the first unit on a page is always admitted.
 *
 * @param {object[]} units
 * @param {number} unitsPerPage           the 25-group page (the user's group page size)
 * @param {number} [rowBudget]            GROUP_PAGE_ROW_BUDGET
 * @param {(u: object) => number} [weightOf]
 * @returns {number[]}
 */
export const groupPageStarts = (units, unitsPerPage, rowBudget = GROUP_PAGE_ROW_BUDGET, weightOf = renderedRowWeight) => {
  const starts = [0]
  let count = 0
  let weight = 0
  ;(units || []).forEach((unit, i) => {
    const w = weightOf(unit)
    if (count > 0 && (count >= unitsPerPage || weight + w > rowBudget)) {
      starts.push(i)
      count = 0
      weight = 0
    }
    count += 1
    weight += w
  })
  return starts
}
